/* Quick Order — B2B order pad.
 *
 * Uses only native storefront endpoints, no app and no backend:
 *   /search/suggest.json  — type-ahead. Runs as the logged-in customer, so it returns
 *                           what THIS buyer is allowed to see. That matters here: the
 *                           store runs Locksmith and BSS product hiding, and a hand-built
 *                           product index would not know about either.
 *   /products/{handle}.js — the resolved product's real options and variants, so the size
 *                           and colour pickers can only offer combinations that exist.
 *   /cart/add.js          — one request for the whole order, with a per-line retry so a
 *                           single sold-out line cannot take down the rest.
 *
 * No prices are requested or rendered. Four layers decide a distributor's price
 * (Wholesale Pricing Discount, BSS login-to-see-price, the distributor surcharge and
 * native B2B catalogs); the cart is the one place they all resolve correctly.
 */
(function () {
  'use strict';

  var SUGGEST_LIMIT = 6;
  var DEBOUNCE_MS = 220;
  var MIN_QUERY = 2;

  function money(n) {
    return String(n);
  }

  function debounce(fn, wait) {
    var t = null;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function esc(s) {
    return String(s == null ? '' : s);
  }

  /* Shopify CDN honours a width param, so a 40px thumbnail does not pull the full shot. */
  function thumb(url, w) {
    if (!url) return '';
    try {
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (w || 80);
    } catch (e) {
      return url;
    }
  }

  class FabianQuickOrder extends HTMLElement {
    constructor() {
      super();
      this.rowsEl = null;
      this.tpl = null;
      this.submitting = false;
    }

    connectedCallback() {
      this.rowsEl = this.querySelector('[data-fqo-rows]');
      this.tpl = this.querySelector('[data-fqo-row-template]');
      if (!this.rowsEl || !this.tpl) return;

      this.maxRows = parseInt(this.dataset.maxRows, 10) || 50;
      var initial = parseInt(this.dataset.initialRows, 10) || 5;

      for (var i = 0; i < initial; i++) this.addRow(false);

      this.querySelector('[data-fqo-add]').addEventListener('click', () => {
        var row = this.addRow(true);
        if (row) row.querySelector('[data-fqo-style]').focus();
      });

      this.querySelector('[data-fqo-reset]').addEventListener('click', () => this.reset());
      this.querySelector('[data-fqo-submit]').addEventListener('click', () => this.submit());
      this.initTabs();
      this.initUpload();
      var syncCart = () => {
        this.cartQuantities().then((m) => {
          this._cart = m;
          Array.prototype.forEach.call(this.rowsEl.children, (row) => this.updateStock(row));
          this.updateSummary();
        });
      };
      syncCart();
      /* The cart can be emptied in another tab or on the cart page itself. */
      window.addEventListener('focus', syncCart);
      window.addEventListener('pageshow', syncCart);

      var modal = document.querySelector('[data-fqo-modal]');
      if (modal) {
        this.querySelector('[data-fqo-instructions]').addEventListener('click', function () {
          if (typeof modal.showModal === 'function') modal.showModal();
        });
        modal.querySelector('[data-fqo-modal-close]').addEventListener('click', function () {
          modal.close();
        });
      }

      /* One document-level listener rather than one per row. */
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAllSuggestions();
      });

      this.updateSummary();
    }

    /* ---------- rows ---------- */

    addRow(focusable) {
      if (this.rowsEl.children.length >= this.maxRows) {
        this.notice('You have reached the maximum of ' + this.maxRows + ' lines.', true);
        return null;
      }
      var frag = this.tpl.content.cloneNode(true);
      var row = frag.querySelector('.fqo__row');
      this.wireRow(row);
      this.rowsEl.appendChild(frag);
      return this.rowsEl.lastElementChild;
    }

    wireRow(row) {
      var styleInput = row.querySelector('[data-fqo-style]');
      var suggest = row.querySelector('[data-fqo-suggest]');
      var qty = row.querySelector('[data-fqo-qty]');

      /* row.state holds the resolved product; nothing is trusted from the text field. */
      row._fqo = { product: null, activeIndex: -1, results: [] };

      var run = debounce(() => this.search(row), DEBOUNCE_MS);
      styleInput.addEventListener('input', () => {
        if (row._fqo.product) this.clearMatch(row);
        run();
      });
      styleInput.addEventListener('focus', () => {
        if (row._fqo.results.length && !row._fqo.product) suggest.hidden = false;
      });
      styleInput.addEventListener('keydown', (e) => this.onKeyDown(e, row));

      row.querySelector('[data-fqo-clear]').addEventListener('click', () => {
        this.clearMatch(row);
        styleInput.value = '';
        styleInput.focus();
      });

      row.querySelector('[data-fqo-copy]').addEventListener('click', () => this.duplicate(row));
      row.querySelector('[data-fqo-remove]').addEventListener('click', () => this.removeRow(row));
      row.querySelector('[data-fqo-view]').addEventListener('click', () => {
        var p = row._fqo.product;
        if (p && p.url) window.open(p.url, '_blank', 'noopener');
      });

      qty.addEventListener('input', () => {
        this.clearError(row);
        this.updateStock(row);
        this.updateSummary();
      });
    }

    removeRow(row) {
      /* Never leave the form with nothing to type into. */
      if (this.rowsEl.children.length === 1) {
        var input = row.querySelector('[data-fqo-style]');
        this.clearMatch(row);
        input.value = '';
        row.querySelector('[data-fqo-qty]').value = '';
        this.clearError(row);
      } else {
        row.remove();
      }
      this.updateSummary();
    }

    duplicate(row) {
      var p = row._fqo.product;
      if (!p) {
        this.notice('Choose a style on that line first, then duplicate it.', true);
        return;
      }
      if (this.rowsEl.children.length >= this.maxRows) {
        this.notice('You have reached the maximum of ' + this.maxRows + ' lines.', true);
        return;
      }
      var frag = this.tpl.content.cloneNode(true);
      var newRow = frag.querySelector('.fqo__row');
      this.wireRow(newRow);
      row.after(frag);

      var inserted = row.nextElementSibling;
      inserted.querySelector('[data-fqo-style]').value = row.querySelector('[data-fqo-style]').value;
      /* Carry the style only — the whole point is the same style in another size. */
      this.applyProduct(inserted, p);
      inserted.querySelector('[data-fqo-qty]').focus();
      this.updateSummary();
    }

    reset() {
      var hasContent = Array.prototype.some.call(this.rowsEl.children, function (r) {
        return r.querySelector('[data-fqo-style]').value.trim() || r.querySelector('[data-fqo-qty]').value.trim();
      });
      if (hasContent && !window.confirm('Clear every line and start over?')) return;

      this.rowsEl.innerHTML = '';
      var initial = parseInt(this.dataset.initialRows, 10) || 5;
      for (var i = 0; i < initial; i++) this.addRow(false);
      this.notice('', false, true);
      this.updateSummary();
    }

    /* ---------- autocomplete ---------- */

    search(row) {
      var input = row.querySelector('[data-fqo-style]');
      var suggest = row.querySelector('[data-fqo-suggest]');
      var q = input.value.trim();

      if (q.length < MIN_QUERY) {
        suggest.hidden = true;
        suggest.innerHTML = '';
        row._fqo.results = [];
        return;
      }

      var url =
        '/search/suggest.json?q=' +
        encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=' +
        SUGGEST_LIMIT +
        '&resources[options][unavailable_products]=last';

      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then((data) => {
          var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          row._fqo.results = products;
          row._fqo.activeIndex = -1;
          this.renderSuggestions(row, products);
        })
        .catch(() => {
          row._fqo.results = [];
          suggest.hidden = true;
        });
    }

    renderSuggestions(row, products) {
      var suggest = row.querySelector('[data-fqo-suggest]');
      suggest.innerHTML = '';

      if (!products.length) {
        var empty = document.createElement('p');
        empty.className = 'fqo__empty';
        empty.textContent = 'No styles matched. Check the number, or search by name.';
        suggest.appendChild(empty);
        suggest.hidden = false;
        return;
      }

      products.forEach((p, i) => {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fqo__option';
        btn.dataset.index = String(i);

        if (p.featured_image && p.featured_image.url) {
          var img = document.createElement('img');
          img.src = thumb(p.featured_image.url, 80);
          img.alt = '';
          img.loading = 'lazy';
          btn.appendChild(img);
        }

        var body = document.createElement('span');
        body.className = 'fqo__option-body';

        var title = document.createElement('span');
        title.className = 'fqo__option-title';
        title.textContent = esc(p.title);
        body.appendChild(title);

        /* Full title on hover. Fabian's style codes differ by one letter — 4372PVPS is a
           coat, 4372PVS is a pant — so a truncated title is genuinely ambiguous at the
           moment of choosing. Rebecca, 2026-08-27: "it could be a coat and it could be a
           pant... I'm just confirming, am I clicking the right thing?"
           Stored on a data attribute rather than `title`, because the browser's own
           tooltip is slow, unstyleable, and would sit on top of the list. */
        btn.dataset.full = esc(p.title);
        if (p.vendor) btn.dataset.vendor = esc(p.vendor);

        /* Vendor is the sub-brand here (Uncommon Chef / Executive Apparel / Fabian),
           which is the fastest way for a distributor to tell near-identical styles apart. */
        if (p.vendor) {
          var sku = document.createElement('span');
          sku.className = 'fqo__option-sku';
          sku.textContent = esc(p.vendor);
          body.appendChild(sku);
        }

        btn.appendChild(body);
        btn.addEventListener('click', () => this.choose(row, p));
        btn.addEventListener('mouseenter', () => this.showTip(btn));
        btn.addEventListener('focus', () => this.showTip(btn));
        btn.addEventListener('mouseleave', () => this.hideTip());
        btn.addEventListener('blur', () => this.hideTip());
        suggest.appendChild(btn);
      });

      suggest.hidden = false;
    }

    /* ---------- tooltip ----------
     * One element for the whole component, positioned against whichever suggestion is
     * hovered. It lives on <body> rather than inside the results list because that list
     * scrolls, and a tooltip inside a scrolling box gets clipped at its edge. */
    ensureTip() {
      if (this.tip) return this.tip;
      var tip = document.createElement('div');
      tip.className = 'fqo__tip';
      tip.setAttribute('role', 'tooltip');
      tip.hidden = true;
      tip.innerHTML = '<span class="fqo__tip-title"></span><span class="fqo__tip-vendor"></span>';
      document.body.appendChild(tip);
      this.tip = tip;
      return tip;
    }

    showTip(el) {
      var full = el.dataset.full;
      if (!full) return;
      /* hideTip() leaves a 140ms timer running. Moving straight from one suggestion to
         the next used to let that old timer fire and blank the tooltip that had just
         opened, so the buyer had to leave the list and come back. Cancel it first. */
      clearTimeout(this._tipT);
      var tip = this.ensureTip();
      tip.querySelector('.fqo__tip-title').textContent = full;
      var v = tip.querySelector('.fqo__tip-vendor');
      v.textContent = el.dataset.vendor || '';
      v.hidden = !el.dataset.vendor;

      tip.hidden = false;
      tip.classList.remove('is-visible', 'is-left');

      var r = el.getBoundingClientRect();
      var t = tip.getBoundingClientRect();
      var gap = 12;

      /* Beside the row, not above it. Above would cover the other suggestions, which is
         exactly what the buyer is scanning when they reach for this. */
      var left = r.right + gap;
      if (left + t.width > window.innerWidth - 12) {
        left = r.left - t.width - gap;
        tip.classList.add('is-left');
      }
      if (left < 12) left = 12;

      /* Vertically centred on the row, clamped to the viewport. */
      var top = r.top + r.height / 2 - t.height / 2;
      if (top < 8) top = 8;
      if (top + t.height > window.innerHeight - 8) top = window.innerHeight - t.height - 8;

      tip.style.top = Math.round(top + window.scrollY) + 'px';
      tip.style.left = Math.round(left + window.scrollX) + 'px';
      tip.style.setProperty(
        '--fqo-tip-arrow',
        Math.round(Math.min(Math.max(r.top + r.height / 2 - top, 14), t.height - 14)) + 'px'
      );

      requestAnimationFrame(function () {
        tip.classList.add('is-visible');
      });
    }

    hideTip() {
      if (!this.tip) return;
      this.tip.classList.remove('is-visible');
      var tip = this.tip;
      clearTimeout(this._tipT);
      this._tipT = setTimeout(function () {
        tip.hidden = true;
      }, 140);
    }

    onKeyDown(e, row) {
      var suggest = row.querySelector('[data-fqo-suggest]');
      if (suggest.hidden) return;
      var opts = suggest.querySelectorAll('.fqo__option');
      if (!opts.length) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var dir = e.key === 'ArrowDown' ? 1 : -1;
        var next = row._fqo.activeIndex + dir;
        if (next < 0) next = opts.length - 1;
        if (next >= opts.length) next = 0;
        row._fqo.activeIndex = next;
        opts.forEach(function (o, i) {
          o.classList.toggle('is-active', i === next);
        });
        opts[next].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        if (row._fqo.activeIndex >= 0) {
          e.preventDefault();
          opts[row._fqo.activeIndex].click();
        }
      } else if (e.key === 'Escape') {
        suggest.hidden = true;
      }
    }

    closeAllSuggestions() {
      this.querySelectorAll('[data-fqo-suggest]').forEach(function (s) {
        s.hidden = true;
      });
      this.hideTip();
    }

    choose(row, summary) {
      var suggest = row.querySelector('[data-fqo-suggest]');
      suggest.hidden = true;
      row.querySelector('[data-fqo-style]').value = summary.title;

      /* The suggest payload is a summary — fetch the product for real option values. */
      var handle = summary.handle || (summary.url || '').split('/products/')[1];
      if (!handle) return;
      handle = handle.split('?')[0];

      fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then((product) => {
          if (!product) {
            this.setError(row, 'That style could not be loaded. Please try again.');
            return;
          }
          this.applyProduct(row, product);
        })
        .catch(() => this.setError(row, 'That style could not be loaded. Please try again.'));
    }

    applyProduct(row, product) {
      row._fqo.product = product;

      var match = row.querySelector('[data-fqo-match]');
      var img = row.querySelector('[data-fqo-thumb]');
      var title = row.querySelector('[data-fqo-title]');
      var view = row.querySelector('[data-fqo-view]');

      var media =
        (product.featured_image && (product.featured_image.url || product.featured_image)) ||
        (product.images && product.images[0]) ||
        '';
      if (media) {
        img.src = thumb(media, 96);
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      title.textContent = esc(product.title);
      title.title = esc(product.title);
      match.hidden = false;
      view.hidden = false;

      this.renderOptions(row, product);
      this.clearError(row);
      this.loadStock(row);
      this.updateSummary();
    }

    clearMatch(row) {
      row._fqo.product = null;
      row._fqo.stock = null;
      this.updateStock(row);
      row.querySelector('[data-fqo-match]').hidden = true;
      row.querySelector('[data-fqo-view]').hidden = true;
      var opts = row.querySelector('[data-fqo-options]');
      opts.hidden = true;
      opts.innerHTML = '';
      row.querySelector('[data-fqo-hint]').hidden = false;
      this.updateSummary();
    }

    /* Build one picker per real product option, so a 3-option dress shirt and a
       1-option apron both work without special-casing. */
    renderOptions(row, product) {
      var wrap = row.querySelector('[data-fqo-options]');
      var hint = row.querySelector('[data-fqo-hint]');
      wrap.innerHTML = '';

      var names = product.options || [];
      /* Shopify returns options as strings on some shapes and objects on others. */
      var optionNames = names.map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });

      var hasRealOptions =
        optionNames.length > 1 || (optionNames.length === 1 && optionNames[0] !== 'Title');

      if (!hasRealOptions) {
        hint.hidden = true;
        wrap.hidden = false;
        var only = document.createElement('p');
        only.className = 'fqo__hint';
        only.style.fontStyle = 'normal';
        only.style.paddingTop = '1.3rem';
        only.textContent = 'One option only';
        wrap.appendChild(only);
        row._fqo.selects = [];
        return;
      }

      var selects = [];
      optionNames.forEach(function (name, idx) {
        var values = [];
        product.variants.forEach(function (v) {
          var val = v.options[idx];
          if (val && values.indexOf(val) === -1) values.push(val);
        });

        var w = document.createElement('div');
        w.className = 'fqo__select-wrap';

        var sel = document.createElement('select');
        sel.className = 'fqo__select';
        sel.setAttribute('aria-label', name);

        var ph = document.createElement('option');
        ph.value = '';
        ph.textContent = name;
        ph.disabled = true;
        ph.selected = true;
        sel.appendChild(ph);

        values.forEach(function (v) {
          var o = document.createElement('option');
          o.value = v;
          o.textContent = v;
          sel.appendChild(o);
        });

        w.appendChild(sel);
        wrap.appendChild(w);
        selects.push(sel);
      });

      row._fqo.selects = selects;
      selects.forEach((s) => {
        s.addEventListener('change', () => {
          this.clearError(row);
          this.markAvailability(row);
          this.updateStock(row);
          this.updateSummary();
        });
      });

      hint.hidden = true;
      wrap.hidden = false;
      this.markAvailability(row);
    }

    /* Show what can actually be ordered, before the buyer commits to a combination.
     *
     * Without this the page reads as broken: on a style where only a few size/colour
     * combinations are in stock, every pick looks valid until Add to cart refuses it.
     * Labelling the dead values turns trial-and-error into one glance. */
    markAvailability(row) {
      var p = row._fqo.product;
      var selects = row._fqo.selects || [];
      if (!p || !selects.length) return;

      var chosen = selects.map(function (s) {
        return s.value;
      });

      selects.forEach(function (sel, idx) {
        Array.prototype.forEach.call(sel.options, function (opt) {
          if (!opt.value) return;

          /* A value is orderable if some available variant carries it while agreeing
             with every OTHER option already chosen. Options the buyer has not set yet
             stay unconstrained, so the first pick is judged across the whole style. */
          var ok = p.variants.some(function (v) {
            if (!v.available) return false;
            if (v.options[idx] !== opt.value) return false;
            return chosen.every(function (val, i) {
              return i === idx || !val || v.options[i] === val;
            });
          });

          var base = opt.dataset.label || opt.textContent;
          opt.dataset.label = base;
          opt.textContent = ok ? base : base + ' — out of stock';
          opt.disabled = !ok;
        });
      });
    }

    /* ---------- validation + submit ---------- */

    resolveVariant(row) {
      var p = row._fqo.product;
      if (!p) return null;
      var selects = row._fqo.selects || [];
      if (!selects.length) return p.variants[0] || null;

      var chosen = selects.map(function (s) {
        return s.value;
      });
      if (chosen.some(function (v) { return !v; })) return null;

      return (
        p.variants.filter(function (v) {
          return chosen.every(function (val, i) {
            return v.options[i] === val;
          });
        })[0] || null
      );
    }

    rowState(row) {
      var styleText = row.querySelector('[data-fqo-style]').value.trim();
      var qtyText = row.querySelector('[data-fqo-qty]').value.trim();
      var product = row._fqo.product;

      if (!styleText && !qtyText && !product) return { kind: 'empty' };

      if (!product) return { kind: 'invalid', blocking: true, message: 'Pick a style from the list to confirm it.' };

      var variant = this.resolveVariant(row);
      if (!variant) return { kind: 'invalid', blocking: true, message: 'Choose every option for this style.' };

      /* Tell them here, not after they press Add to cart. Fabian's catalogue has plenty of
         styles where only a few size/colour combinations are in stock, so silently
         accepting a dead combination and failing at submit wastes the buyer's time. */
      if (variant.available === false) {
        return {
          kind: 'invalid',
          blocking: false,
          message: 'That combination is out of stock. Try another size or colour.'
        };
      }

      var qty = parseInt(qtyText, 10);
      if (!qtyText || isNaN(qty) || qty < 1) {
        return { kind: 'invalid', blocking: true, message: 'Enter a quantity of 1 or more.' };
      }

      /* Stock already sitting in this cart is not stock that can be added again. Without
         this the footer cheerfully offers 112 items while the row right above it says
         every one of them is already in the cart, and the button invites a dead click. */
      var stock = row._fqo.stock && row._fqo.stock[variant.id];
      if (stock && stock.tracked && !stock.oversell) {
        var held = (this._cart && this._cart[variant.id]) || 0;
        var spare = Math.max(0, (stock.qty > 0 ? stock.qty : 0) - held);
        if (spare === 0) {
          return {
            kind: 'invalid',
            blocking: false,
            message: held
              ? 'All ' + stock.qty + ' of these are already in your cart. Open your cart to check out, or remove this line.'
              : 'This size is out of stock. Try another size or colour.'
          };
        }
      }

      return { kind: 'ready', variant: variant, quantity: qty, product: product };
    }

    updateSummary() {
      var lines = 0;
      var units = 0;
      var invalid = 0;

      Array.prototype.forEach.call(this.rowsEl.children, (row) => {
        var st = this.rowState(row);
        if (st.kind === 'ready') {
          lines++;
          units += st.quantity;
        } else if (st.kind === 'invalid') {
          invalid++;
        }
      });

      var summary = this.querySelector('[data-fqo-summary]');
      var submit = this.querySelector('[data-fqo-submit]');

      /* Never say "no items" when the buyer has clearly entered something. Before
         2026-08-27 an out-of-stock line left this reading "No items yet" with the button
         greyed out and no reason given, which reads as a broken page rather than as a
         stock problem. Count the lines that need attention and say so. */
      if (lines) {
        summary.innerHTML =
          '<strong>' +
          lines +
          '</strong> ' +
          (lines === 1 ? 'line' : 'lines') +
          ' &middot; <strong>' +
          units +
          '</strong> ' +
          (units === 1 ? 'item' : 'items') +
          (invalid
            ? ' &middot; <span class="fqo__warn">' +
              invalid +
              (invalid === 1 ? " line can't" : " lines can't") +
              ' be added yet</span>'
            : '');
      } else if (invalid) {
        summary.innerHTML =
          '<span class="fqo__warn">' +
          invalid +
          (invalid === 1 ? " line can't be added yet" : " lines can't be added yet") +
          ' &mdash; see the note under ' +
          (invalid === 1 ? 'it' : 'each one') +
          '</span>';
      } else {
        summary.textContent = 'No items yet.';
      }

      /* Stay clickable while anything is entered, so pressing it explains what is wrong
         instead of leaving a dead button. */
      submit.disabled = (lines === 0 && invalid === 0) || this.submitting;
      submit.textContent = this.submitting ? 'Adding…' : 'Add to cart';
    }

    setError(row, message) {
      var el = row.querySelector('[data-fqo-error]');
      el.textContent = message;
      el.hidden = false;
      row.classList.add('is-invalid');
    }

    clearError(row) {
      var el = row.querySelector('[data-fqo-error]');
      el.hidden = true;
      el.textContent = '';
      row.classList.remove('is-invalid');
    }

    notice(message, isError, hide, showCart) {
      var el = this.querySelector('[data-fqo-notice]');
      var text = el.querySelector('[data-fqo-notice-text]');
      var cart = el.querySelector('[data-fqo-notice-cart]');

      if (hide || !message) {
        el.hidden = true;
        if (text) text.textContent = '';
        if (cart) cart.hidden = true;
        el.classList.remove('is-error');
        return;
      }

      if (text) text.textContent = message;
      else el.textContent = message;
      /* Anything that reached the cart needs a way through to it, otherwise a trimmed
         line strands the buyer on this page with a button that can only fail. */
      if (cart) cart.hidden = !showCart;
      el.hidden = false;
      el.classList.toggle('is-error', !!isError);
    }

    /* ---------- tabs ---------- */

    initTabs() {
      var tabs = Array.prototype.slice.call(this.querySelectorAll('[data-fqo-tab]'));
      var panels = Array.prototype.slice.call(this.querySelectorAll('[data-fqo-panel]'));
      if (!tabs.length) return;

      this.showTab = function (name) {
        tabs.forEach(function (t) {
          var on = t.dataset.fqoTab === name;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (pn) {
          pn.hidden = pn.dataset.fqoPanel !== name;
        });
      };

      tabs.forEach((t) => {
        t.addEventListener('click', () => this.showTab(t.dataset.fqoTab));
      });
    }

    /* ---------- upload order ---------- */

    initUpload() {
      var tpl = this.querySelector('[data-fqo-template]');
      var file = this.querySelector('[data-fqo-file]');
      if (!tpl || !file) return;

      tpl.addEventListener('click', () => this.downloadTemplate());
      file.addEventListener('change', () => {
        var f = file.files && file.files[0];
        if (f) this.readOrderFile(f);
        /* Let the same filename be picked again after the buyer corrects it. */
        file.value = '';
      });
    }

    downloadTemplate() {
      var rows = [
        'UPC,Style,Color,Size,Length,Quantity',
        '844513010459,,,,,6',
        ',U4000,BLACK,M,,12',
        ',4372PVPS,BLACK,34,REGULAR,8'
      ].join('\r\n');

      /* The BOM keeps Excel from turning a UPC into 8.44513E+11, which is exactly how the
         restock file arrived unusable in August. */
      var blob = new Blob(['\uFEFF' + rows], { type: 'text/csv;charset=utf-8;' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'fabian-quick-order-template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 1000);
    }

    /* ---------- spreadsheet reading ---------- */

    /* Everyone opens the template in Excel, and Excel saves .xlsx. Asking buyers to
       remember "Save as CSV" was the whole reason the first upload attempt failed six
       times in a row, so read the workbook directly. An .xlsx is a zip of XML, and the
       browser can inflate it on its own: no library, no CDN. */
    readOrderFile(file) {
      var label = this.querySelector('[data-fqo-file-label]');
      if (label) label.textContent = file.name;
      this.uploadReport('Reading ' + file.name + '…');

      file
        .slice(0, 4)
        .arrayBuffer()
        .then((head) => {
          var sig = new Uint8Array(head);
          var isZip = sig[0] === 0x50 && sig[1] === 0x4b && sig[2] === 0x03 && sig[3] === 0x04;
          if (!isZip) {
            return file.text().then((text) => this.consumeCsv(text));
          }
          if (typeof DecompressionStream !== 'function') {
            this.uploadReport(
              'This browser cannot open Excel files. In Excel choose File, then Save As, ' +
                'and pick CSV, then upload that.',
              true
            );
            return null;
          }
          return file.arrayBuffer().then((buf) => this.readXlsx(buf));
        })
        .catch(() => this.uploadReport('That file could not be read. Please try again.', true));
    }

    /* Walks the zip's central directory rather than guessing at offsets, so a workbook
       with extra fields or a comment still reads correctly. */
    unzip(buffer, wanted) {
      var view = new DataView(buffer);
      var bytes = new Uint8Array(buffer);
      var eocd = -1;

      for (var i = buffer.byteLength - 22; i >= 0 && i > buffer.byteLength - 66000; i--) {
        if (view.getUint32(i, true) === 0x06054b50) {
          eocd = i;
          break;
        }
      }
      if (eocd === -1) return Promise.reject(new Error('not a zip'));

      var count = view.getUint16(eocd + 10, true);
      var start = view.getUint32(eocd + 16, true);
      var entries = [];
      var at = start;

      for (var n = 0; n < count && at + 46 <= buffer.byteLength; n++) {
        if (view.getUint32(at, true) !== 0x02014b50) break;
        var method = view.getUint16(at + 10, true);
        var compressed = view.getUint32(at + 20, true);
        var nameLen = view.getUint16(at + 28, true);
        var extraLen = view.getUint16(at + 30, true);
        var commentLen = view.getUint16(at + 32, true);
        var localAt = view.getUint32(at + 42, true);
        var name = new TextDecoder().decode(bytes.subarray(at + 46, at + 46 + nameLen));
        entries.push({ name: name, method: method, compressed: compressed, localAt: localAt });
        at += 46 + nameLen + extraLen + commentLen;
      }

      var pick = wanted(entries.map(function (e) {
        return e.name;
      }));

      var read = (name) => {
        var e = entries.filter(function (x) {
          return x.name === name;
        })[0];
        if (!e) return Promise.resolve('');

        /* The local header repeats the name and extra fields, and its lengths are the
           ones that count: some writers pad the central copy differently. */
        var lnameLen = view.getUint16(e.localAt + 26, true);
        var lextraLen = view.getUint16(e.localAt + 28, true);
        var from = e.localAt + 30 + lnameLen + lextraLen;
        var slice = bytes.subarray(from, from + e.compressed);

        if (e.method === 0) return Promise.resolve(new TextDecoder().decode(slice));
        if (e.method !== 8) return Promise.reject(new Error('unsupported compression'));

        var stream = new Blob([slice]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
        return new Response(stream).text();
      };

      return Promise.all(pick.map(read)).then(function (texts) {
        var out = {};
        pick.forEach(function (name, idx) {
          out[name] = texts[idx];
        });
        return out;
      });
    }

    readXlsx(buffer) {
      return this.unzip(buffer, function (names) {
        var sheet =
          names
            .filter(function (n) {
              return /^xl\/worksheets\/sheet\d+\.xml$/.test(n);
            })
            .sort()[0] || '';
        var shared = names.indexOf('xl/sharedStrings.xml') !== -1 ? 'xl/sharedStrings.xml' : '';
        return [sheet, shared].filter(Boolean);
      })
        .then((files) => {
          var sheetName = Object.keys(files).filter(function (n) {
            return n.indexOf('worksheets') !== -1;
          })[0];
          if (!sheetName) {
            this.uploadReport('That workbook has no sheet we could read.', true);
            return;
          }

          var parser = new DOMParser();
          var strings = [];
          if (files['xl/sharedStrings.xml']) {
            var sdoc = parser.parseFromString(files['xl/sharedStrings.xml'], 'application/xml');
            Array.prototype.forEach.call(sdoc.getElementsByTagNameNS('*', 'si'), function (si) {
              var t = si.getElementsByTagNameNS('*', 't');
              var s = '';
              for (var k = 0; k < t.length; k++) s += t[k].textContent;
              strings.push(s);
            });
          }

          var doc = parser.parseFromString(files[sheetName], 'application/xml');
          var rows = [];

          Array.prototype.forEach.call(doc.getElementsByTagNameNS('*', 'row'), function (row) {
            var cells = [];
            Array.prototype.forEach.call(row.getElementsByTagNameNS('*', 'c'), function (c) {
              /* Excel leaves empty cells out entirely, so place each one by its column
                 letter or a blank column would shift every value after it. */
              var ref = c.getAttribute('r') || '';
              var letters = ref.replace(/[0-9]/g, '');
              var idx = 0;
              for (var k = 0; k < letters.length; k++) {
                idx = idx * 26 + (letters.toUpperCase().charCodeAt(k) - 64);
              }
              idx = idx > 0 ? idx - 1 : cells.length;

              var type = c.getAttribute('t');
              var value = '';
              if (type === 'inlineStr') {
                var is = c.getElementsByTagNameNS('*', 't');
                for (var m = 0; m < is.length; m++) value += is[m].textContent;
              } else {
                var v = c.getElementsByTagNameNS('*', 'v')[0];
                value = v ? v.textContent : '';
                if (type === 's') value = strings[parseInt(value, 10)] || '';
              }

              while (cells.length < idx) cells.push('');
              cells[idx] = value;
            });
            rows.push(cells);
          });

          this.consumeRows(
            rows.filter(function (r) {
              return r.some(function (cell) {
                return String(cell).trim() !== '';
              });
            })
          );
        })
        .catch(() => {
          this.uploadReport(
            'That Excel file could not be opened. In Excel choose File, then Save As, and pick ' +
              'CSV, then upload that instead.',
            true
          );
        });
    }

    /* Hand-rolled rather than split(','), because a style name with a comma in it would
       quietly shift every column after it. */
    parseCsv(text) {
      text = text.replace(/^\uFEFF/, '');
      var rows = [];
      var row = [];
      var field = '';
      var inQuotes = false;

      for (var i = 0; i < text.length; i++) {
        var c = text.charAt(i);
        if (inQuotes) {
          if (c === '"') {
            if (text.charAt(i + 1) === '"') {
              field += '"';
              i++;
            } else {
              inQuotes = false;
            }
          } else {
            field += c;
          }
        } else if (c === '"') {
          inQuotes = true;
        } else if (c === ',') {
          row.push(field);
          field = '';
        } else if (c === '\n') {
          row.push(field);
          rows.push(row);
          row = [];
          field = '';
        } else if (c !== '\r') {
          field += c;
        }
      }
      if (field !== '' || row.length) {
        row.push(field);
        rows.push(row);
      }

      return rows.filter(function (r) {
        return r.some(function (cell) {
          return String(cell).trim() !== '';
        });
      });
    }

    consumeCsv(text) {
      this.consumeRows(this.parseCsv(text));
    }

    consumeRows(rows) {
      if (rows.length < 2) {
        this.uploadReport(
          'That file has no order lines in it. Start from the template and add one line per size.',
          true
        );
        return;
      }

      var header = rows[0].map(function (h) {
        return String(h).trim().toLowerCase();
      });
      var col = function (names) {
        for (var i = 0; i < names.length; i++) {
          var at = header.indexOf(names[i]);
          if (at !== -1) return at;
        }
        return -1;
      };

      var iUpc = col(['upc', 'barcode']);
      var iStyle = col(['style', 'style number', 'style #', 'sku']);
      var iQty = col(['quantity', 'qty']);
      var iColor = col(['color', 'colour']);
      var iSize = col(['size']);
      var iLength = col(['length']);

      if (iQty === -1 || (iUpc === -1 && iStyle === -1)) {
        this.uploadReport(
          'That file is missing the columns we need. It needs a Quantity column, and either a UPC column or a Style column. The template has them ready.',
          true
        );
        return;
      }

      var at = function (r, idx) {
        return idx === -1 ? '' : String(r[idx] === undefined ? '' : r[idx]).trim();
      };

      var entries = [];
      var skipped = 0;

      rows.slice(1).forEach(function (r, n) {
        var qty = parseInt(at(r, iQty), 10);
        var upc = at(r, iUpc);
        var style = at(r, iStyle);

        if (!upc && !style) return;
        if (!qty || qty < 1) {
          skipped++;
          return;
        }
        entries.push({
          line: n + 2,
          upc: upc,
          style: style,
          color: at(r, iColor),
          size: at(r, iSize),
          length: at(r, iLength),
          quantity: qty
        });
      });

      if (!entries.length) {
        this.uploadReport('No line in that file had a quantity of 1 or more.', true);
        return;
      }

      this.uploadReport(
        'Matching ' + entries.length + (entries.length === 1 ? ' line' : ' lines') + ' against the catalogue…'
      );
      this.resolveCsv(entries, skipped);
    }

    /* One lookup per distinct style or UPC, cached, because a real order is usually one
       style repeated down a size run. */
    resolveCsv(entries, skipped) {
      var cache = {};
      var results = [];
      var chain = Promise.resolve();

      /* Storefront search is fuzzy: asking it for 4372PVPS happily answers with
         4372PVS-SX. On a typed line the buyer sees the suggestion and picks; from a file
         nobody is watching, so a near miss would quietly order the wrong style. Only
         accept a product that really carries this code, in its title or in a SKU. */
      var norm = function (v) {
        return String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      };

      var carriesCode = function (product, key) {
        var k = norm(key);
        if (!k) return false;
        if (norm(product.title).indexOf(k) !== -1) return true;
        if (norm(product.handle).indexOf(k) !== -1) return true;
        return product.variants.some(function (v) {
          return norm(v.sku).indexOf(k) !== -1 || norm(v.barcode) === k;
        });
      };

      var fetchProduct = function (url) {
        var handle = String(url || '').split('/products/')[1];
        if (!handle) return Promise.resolve(null);
        return fetch('/products/' + handle.split('?')[0] + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          });
      };

      var lookup = function (key, isUpc) {
        if (cache[key]) return cache[key];
        cache[key] = fetch(
          '/search/suggest.json?q=' + encodeURIComponent(key) + '&resources[type]=product&resources[limit]=5'
        )
          .then(function (r) {
            return r.json();
          })
          .then(function (d) {
            var list =
              (d && d.resources && d.resources.results && d.resources.results.products) || [];
            if (!list.length) return null;

            /* Titles carry the style number on this catalogue, so the right candidate can
               usually be picked before spending a request on it. */
            var k = norm(key);
            var shortlist = list.filter(function (hit) {
              return norm(hit.title).indexOf(k) !== -1;
            });
            var ordered = shortlist.concat(
              list.filter(function (hit) {
                return shortlist.indexOf(hit) === -1;
              })
            );

            var step = function (i) {
              if (i >= ordered.length) return null;
              return fetchProduct(ordered[i].url).then(function (product) {
                if (!product) return step(i + 1);
                if (isUpc || carriesCode(product, key)) return product;
                return step(i + 1);
              });
            };
            return step(0);
          })
          .catch(function () {
            return null;
          });
        return cache[key];
      };

      entries.forEach(function (e) {
        chain = chain.then(function () {
          return lookup(e.upc || e.style, !!e.upc).then(function (product) {
            if (!product) {
              results.push({
                entry: e,
                error: e.upc
                  ? 'no product carries UPC ' + e.upc
                  : 'no style in the catalogue is numbered "' + e.style + '"'
              });
              return;
            }

            if (e.upc) {
              var byCode =
                product.variants.filter(function (v) {
                  return String(v.barcode || '') === e.upc;
                })[0] || null;
              if (!byCode) {
                results.push({ entry: e, error: 'UPC ' + e.upc + ' is not on ' + product.title });
                return;
              }
              results.push({ entry: e, product: product, variant: byCode });
              return;
            }

            var optionNames = (product.options || []).map(function (o) {
              return typeof o === 'string' ? o : o.name;
            });
            var byPosition = [e.color, e.size, e.length];

            var wanted = optionNames.map(function (name, idx) {
              var lower = String(name).toLowerCase();
              if (lower.indexOf('colour') !== -1 || lower.indexOf('color') !== -1) return e.color;
              if (lower.indexOf('size') !== -1) return e.size;
              if (lower.indexOf('length') !== -1) return e.length;
              return byPosition[idx] || '';
            });

            var missing = optionNames.filter(function (name, idx) {
              return !wanted[idx];
            });
            if (missing.length) {
              results.push({
                entry: e,
                error: product.title + ' needs ' + missing.join(' and ') + ' filled in'
              });
              return;
            }

            var variant =
              product.variants.filter(function (v) {
                return wanted.every(function (w, idx) {
                  return String(v.options[idx] || '').toLowerCase() === String(w).toLowerCase();
                });
              })[0] || null;

            if (!variant) {
              results.push({
                entry: e,
                error: wanted.join(' / ') + ' is not a combination ' + product.title + ' comes in'
              });
              return;
            }

            results.push({ entry: e, product: product, variant: variant });
          });
        });
      });

      chain.then(() => this.applyCsvResults(results, skipped));
    }

    applyCsvResults(results, skipped) {
      var good = results.filter(function (r) {
        return r.variant;
      });
      var bad = results.filter(function (r) {
        return !r.variant;
      });

      if (good.length) {
        /* Start from a clean form so an upload never stacks on top of what is already typed. */
        this.rowsEl.innerHTML = '';
        good.forEach((r) => {
          var row = this.addRow(false);
          row.querySelector('[data-fqo-style]').value = r.product.title;
          this.applyProduct(row, r.product);
          (row._fqo.selects || []).forEach(function (sel, idx) {
            if (r.variant.options[idx]) sel.value = r.variant.options[idx];
          });
          this.markAvailability(row);
          row.querySelector('[data-fqo-qty]').value = r.entry.quantity;
        });
        /* Always leave one empty line to type into. */
        this.addRow(false);
        this.updateSummary();
      }

      var html = '';
      if (good.length) {
        html +=
          '<p class="fqo__upload-ok"><strong>' +
          good.length +
          '</strong> ' +
          (good.length === 1 ? 'line is' : 'lines are') +
          ' in the order form. Check it over, then add it to your cart.</p>';
      }
      if (skipped) {
        html +=
          '<p class="fqo__upload-skip">' +
          skipped +
          (skipped === 1 ? ' row was' : ' rows were') +
          ' skipped for having no quantity.</p>';
      }
      if (bad.length) {
        html +=
          '<p class="fqo__upload-bad"><strong>' +
          bad.length +
          '</strong> ' +
          (bad.length === 1 ? 'line' : 'lines') +
          ' could not be matched:</p><ul class="fqo__upload-list">';
        bad.forEach(function (r) {
          html += '<li>Row ' + r.entry.line + ' — ' + esc(r.error) + '</li>';
        });
        html += '</ul>';
      }
      if (!good.length && !bad.length) html = '<p>Nothing in that file could be used.</p>';

      this.uploadReport(html, bad.length > 0 && good.length === 0, true);

      if (good.length && this.showTab) {
        this.showTab('input');
        this.notice(
          good.length +
            (good.length === 1 ? ' line was' : ' lines were') +
            ' loaded from your file. Nothing is in your cart yet.',
          false
        );
      }
    }

    uploadReport(html, isError, asHtml) {
      var el = this.querySelector('[data-fqo-upload-report]');
      if (!el) return;
      if (asHtml) el.innerHTML = html;
      else el.textContent = html;
      el.hidden = false;
      el.classList.toggle('is-error', !!isError);
    }

    /* ---------- stock ---------- */

    /* /products/x.js does not carry inventory_quantity, but Liquid does, so the numbers
       come from a tiny section fetched through the Section Rendering API. One request per
       style, cached next to the product itself. */
    fetchInventory(handle) {
      this._inv = this._inv || {};
      if (this._inv[handle]) return this._inv[handle];

      this._inv[handle] = fetch('/products/' + handle + '?sections=fabian-inventory')
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          var html = data && data['fabian-inventory'];
          if (!html) return null;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('[data-fqo-inventory]');
          if (!node) return null;
          var map = {};
          JSON.parse(node.textContent).forEach(function (v) {
            map[v.id] = v;
          });
          return map;
        })
        .catch(function () {
          return null;
        });

      return this._inv[handle];
    }

    loadStock(row) {
      var product = row._fqo.product;
      if (!product || !product.handle) return;
      this.fetchInventory(product.handle).then((map) => {
        /* The buyer may have cleared or changed the line while this was in flight. */
        if (row._fqo.product !== product) return;
        row._fqo.stock = map;
        this.updateStock(row);
      });
    }

    /* Says how many are really there once a size is settled, and caps the number box, so
       the buyer finds out before pressing Add to cart rather than after Shopify has
       already trimmed the line. Deliberately a warning, not a block: ordering 400 knowing
       293 will come is a legitimate thing to want. */
    updateStock(row) {
      var el = row.querySelector('[data-fqo-stock]');
      var qtyEl = row.querySelector('[data-fqo-qty]');
      if (!el || !qtyEl) return;

      var hide = function () {
        el.hidden = true;
        el.textContent = '';
        el.classList.remove('is-warn');
        qtyEl.removeAttribute('max');
      };

      var map = row._fqo.stock;
      var variant = row._fqo.product ? this.resolveVariant(row) : null;
      if (!map || !variant) return hide();

      var info = map[variant.id];
      if (!info || !info.tracked || info.oversell) return hide();

      /* inventory_quantity is stock on hand, and a cart does not draw it down, so a line
         that already put 293 in the cart would otherwise still read "293 in stock" and
         invite a second click that cannot work. Take off what this cart is holding. */
      var onHand = info.qty > 0 ? info.qty : 0;
      var mine = (this._cart && this._cart[variant.id]) || 0;
      var left = Math.max(0, onHand - mine);
      qtyEl.setAttribute('max', String(left));

      if (left === 0) {
        el.textContent = mine
          ? 'All ' + onHand + ' of these are already in your cart'
          : 'Out of stock';
        el.classList.add('is-warn');
        el.hidden = false;
        return;
      }

      var want = parseInt(qtyEl.value, 10);
      if (want && want > left) {
        el.textContent = 'Only ' + left + ' available. Asking for ' + want + ' will add ' + left + '.';
        el.classList.add('is-warn');
      } else {
        el.textContent = mine ? left + ' still available' : left + ' in stock';
        el.classList.remove('is-warn');
      }
      el.hidden = false;
    }

    /* After a line has been trimmed, the cached figure is stale and the row would go on
       claiming stock that is now in the cart. Re-read it so the row tells the truth. */
    refreshStock(handles) {
      this._inv = this._inv || {};
      var jobs = handles.map((h) => {
        delete this._inv[h];
        return this.fetchInventory(h);
      });
      jobs.push(this.cartQuantities().then((m) => {
        this._cart = m;
      }));
      return Promise.all(jobs).then(() => {
        Array.prototype.forEach.call(this.rowsEl.children, (row) => {
          var p = row._fqo.product;
          if (!p || handles.indexOf(p.handle) === -1) return;
          this.fetchInventory(p.handle).then((map) => {
            row._fqo.stock = map;
            this.updateStock(row);
          });
        });
      });
    }

    /* ---------- cart helpers ---------- */

    cartQuantities() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var map = {};
          (cart.items || []).forEach(function (i) {
            map[i.variant_id] = (map[i.variant_id] || 0) + i.quantity;
          });
          return map;
        });
    }

    /* The header count is rendered by Liquid, so adding without leaving the page leaves
       the old number sitting there. Re-render that one section and swap only the count
       bubble: Fabian's header carries two different cart icons and replacing their whole
       contents would reshape one of them. */
    refreshCartCount() {
      return fetch('/?sections=cart-icon-bubble')
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var html = data['cart-icon-bubble'];
          if (typeof html !== 'string') return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.cart-count-bubble');
          Array.prototype.forEach.call(document.querySelectorAll('#cart-icon-bubble'), function (link) {
            var current = link.querySelector('.cart-count-bubble');
            if (fresh) {
              if (current) current.parentNode.replaceChild(fresh.cloneNode(true), current);
              else link.appendChild(fresh.cloneNode(true));
            } else if (current) {
              current.parentNode.removeChild(current);
            }
          });
        })
        .catch(function () {});
    }

    clearRow(row) {
      this.clearMatch(row);
      row.querySelector('[data-fqo-style]').value = '';
      row.querySelector('[data-fqo-qty]').value = '';
      this.clearError(row);
    }

    submit() {
      if (this.submitting) return;

      this.submitting = true;
      this.updateSummary();
      this.notice('', false, true);

      /* Whatever happens next, the button has to come back. A stalled request used to
         leave it reading "Adding..." for good, with no way out but a reload. */
      var settled = false;
      var release = (message, isError, showCart) => {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        this.submitting = false;
        if (message) this.notice(message, isError !== false, false, !!showCart);
        this.updateSummary();
      };
      var guard = setTimeout(function () {
        release('That is taking longer than it should. Nothing was changed, so please try again.');
      }, 20000);

      /* Read the cart first, before judging any line. Deciding "all of those are already
         in your cart" from a cached figure would refuse a perfectly good order the moment
         that figure went stale. */
      this.cartQuantities()
        .then((before) => {
          this._cart = before;

          var lines = [];
          var firstBad = null;
          var unavailable = 0;

          Array.prototype.forEach.call(this.rowsEl.children, (row) => {
            this.clearError(row);
            var st = this.rowState(row);
            if (st.kind === 'invalid') {
              this.setError(row, st.message);
              /* Something the buyer can type their way out of stops the whole order; a
                 line that is merely out of stock only stops itself. */
              if (st.blocking === false) unavailable++;
              else if (!firstBad) firstBad = row;
            } else if (st.kind === 'ready') {
              lines.push({
                id: st.variant.id,
                quantity: st.quantity,
                row: row,
                handle: st.product.handle
              });
            }
          });

          if (firstBad) {
            release('Some lines are not filled in yet.');
            firstBad.querySelector('[data-fqo-style]').focus();
            return null;
          }

          if (!lines.length) {
            release(
              unavailable
                ? unavailable === 1
                  ? 'That line cannot be added right now. The note under it says why.'
                  : 'None of these lines can be added right now. The note under each one says why.'
                : null,
              true,
              unavailable > 0
            );
            return null;
          }

          var payload = lines.map(function (l) {
            return { id: l.id, quantity: l.quantity };
          });

          /* /cart/add.js trims each line down to whatever stock is left and STILL answers
             422 whenever it had to trim anything. Retrying on 422 therefore added every
             good line a second time, which is how ten pairs became twenty on 2026-09-03.
             So never retry: read the cart again and let the difference say what landed. */
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: payload })
          })
            .then(() => this.cartQuantities())
            .then((after) => {
              if (settled) return;
              settled = true;
              clearTimeout(guard);
              this.reportOutcome(lines, before, after, unavailable);
            });
        })
        .catch(() =>
          release('Your cart could not be reached, so nothing was changed. Please try again.')
        );
    }

    /* Shopify merges repeat variants into a single cart line, so measure the gain per
       variant and then hand those units out to the rows that asked for them, top down. */
    reportOutcome(lines, before, after, skipped) {
      this._cart = after;
      var pool = {};
      lines.forEach(function (l) {
        if (pool[l.id] === undefined) pool[l.id] = (after[l.id] || 0) - (before[l.id] || 0);
      });

      var unitsAdded = 0;
      var stuck = 0;

      lines.forEach((l) => {
        var got = Math.max(0, Math.min(pool[l.id], l.quantity));
        pool[l.id] -= got;
        unitsAdded += got;

        if (got === l.quantity) {
          this.clearRow(l.row);
          return;
        }

        stuck++;
        if (got > 0) {
          /* Leave the shortfall in the box so pressing Add again asks only for what is
             still missing, never for the units already sitting in the cart. */
          l.row.querySelector('[data-fqo-qty]').value = l.quantity - got;
          this.setError(
            l.row,
            l.quantity + ' requested. ' + got + ' available and added to your cart. ' +
              (l.quantity - got) + ' could not be added.'
          );
        } else {
          this.setError(
            l.row,
            l.quantity + ' requested, but this size is out of stock. Nothing was added for this line.'
          );
        }
      });

      this.submitting = false;

      /* Lines that were never sent because nothing is left in stock still have to be
         accounted for. Leaving the page here would drop part of the order silently. */
      stuck += skipped || 0;

      if (!stuck) {
        this.updateSummary();
        window.location.href = '/cart';
        return;
      }

      var msg = unitsAdded
        ? unitsAdded + ' ' + (unitsAdded === 1 ? 'item went' : 'items went') + ' into your cart. '
        : 'Nothing went into your cart. ';
      msg += stuck + ' ' + (stuck === 1 ? 'line' : 'lines') + ' could not be added, ' +
        (stuck === 1 ? 'and the note under it says why.' : 'and the note under each says why.');

      this.notice(msg, true, false, unitsAdded > 0);
      this.refreshCartCount();
      this.updateSummary();

      var touched = [];
      lines.forEach(function (l) {
        if (l.handle && touched.indexOf(l.handle) === -1) touched.push(l.handle);
      });
      if (touched.length) this.refreshStock(touched);
    }
  }

  if (!customElements.get('fabian-quick-order')) {
    customElements.define('fabian-quick-order', FabianQuickOrder);
  }
})();
